/**
 * Estado da aplicacao e o unico lugar que o muta.
 *
 * Responsabilidade: guardar o que a tela desenha, e expor UM setter por
 * mutacao. `ws.ts` e as views chamam esses setters; ninguem escreve no estado
 * por fora.
 *
 * NAO e responsabilidade deste arquivo: falar HTTP (`api.ts`), manter socket
 * (`ws.ts`), desenhar (`views/`) ou serializar cache (`cache.ts`, que este
 * modulo chama).
 *
 * ## Regra da mutacao unica, do lado do navegador
 *
 * A mesma disciplina do firmware e da API: um valor mutavel tem exatamente um
 * setter, e os transportes sao parsers finos. Aqui existem dois transportes
 * para o MESMO fato — o relay WebSocket e a resposta do REST. Um upload
 * confirmado chega por `aplicarUpload()` vindo do socket, e a mesma linha
 * chega por `definirUploads()` depois de um `GET`. As duas convergem no mesmo
 * setter para nao existirem duas nocoes de "lista de uploads" que divergem na
 * primeira diferenca de ordenacao.
 *
 * ## `fonte` existe porque o gate exige honestidade
 *
 * `fonte: "cache"` diz que o que esta na tela veio do disco, nao do servidor.
 * Nenhuma tela pode inferir isso de outra coisa: um snapshot com dado de
 * ontem e indistinguivel de um snapshot de agora de um aparelho parado.
 */

import * as cache from "./cache";
import { MAX_AMOSTRAS } from "./cache";
import type {
  AlertaOut,
  AparelhoNoSnapshot,
  CommandOut,
  EstadoAparelho,
  PrefsNotificacao,
  ReleaseOut,
  TelemetrySample,
  UploadOut,
  UserOut,
} from "./tipos";

export type EstadoConexao = "inicial" | "conectando" | "ao-vivo" | "sem-rede";

/**
 * Janela que o timelapse esta mostrando — o que o seletor de periodo escolhe.
 *
 * Fica no estado, e nao dentro da view, porque e ela que decide QUAL GET de
 * uploads e feito: quem busca e o efeito unico em `acoes.acompanharTimelapse()`,
 * do mesmo jeito que a metrica aberta decide o GET do grafico. Uma copia da
 * janela dentro da view daria duas nocoes de "periodo" que divergiriam na
 * primeira troca de aparelho.
 *
 * `dias: null` e "tudo o que o plano ainda guarda". No modo `datas`, as duas
 * pontas sao `YYYY-MM-DD` e qualquer uma pode vir vazia (janela aberta daquele
 * lado).
 */
export type PeriodoTimelapse =
  | { modo: "relativo"; dias: number | null }
  | { modo: "datas"; de: string; ate: string };

/**
 * Janela que o grafico de 24 h — agora de periodo escolhivel — esta mostrando.
 *
 * Mesma ideia de `PeriodoTimelapse`, ver o comentario acima: fica no estado, e
 * nao dentro da view, porque e ela que decide o GET (`since`/`until`/`bucket`)
 * em `acoes.acompanharGrafico()`. `horas` e o unico numero das opcoes fixas
 * (6/24/168/720); o modo `datas` usa `datetime-local`, e nao so `date` como no
 * timelapse, porque aqui a janela pode ser mais curta que um dia.
 */
export type PeriodoGrafico =
  | { modo: "relativo"; horas: number }
  | { modo: "datas"; de: string; ate: string };

export interface Estado {
  sessao: cache.SessaoLembrada | null;
  conexao: EstadoConexao;
  /** De onde veio o que esta na tela AGORA. */
  fonte: "cache" | "ao-vivo";
  /** Quando o dado na tela foi produzido: chegada do quadro, ou `gravadoEm`. */
  atualizadoEm: number | null;
  latenciaMs: number | null;
  /** Mensagem para o usuario — erro de comando, recusa da API. */
  aviso: string | null;
  aparelhos: AparelhoNoSnapshot[];
  selecionado: string | null;
  telemetria: Record<string, TelemetrySample[]>;
  /**
   * A serie que o grafico de periodo escolhivel desenha AGORA. So em memoria.
   *
   * Uma so, e nao um mapa por aparelho: so existe UM grafico aberto por vez, e
   * a `chave` (aparelho + periodo) e o que decide se o que esta guardado ainda
   * serve. Trocar de aparelho OU de periodo troca a chave, e a serie anterior
   * e substituida — nunca emendada com a nova, que teria outro `bucket` e
   * casaria `ts` por coincidencia com valores de outra agregacao.
   *
   * E SEPARADA da `telemetria` de proposito: aquela e a cauda curta que
   * alimenta as sparklines e vai para o localStorage (~2 h, ver
   * `MAX_AMOSTRAS`); esta pode cobrir 30 dias e nunca toca o disco.
   */
  serie: { chave: string; amostras: TelemetrySample[] } | null;
  /** Qual metrica esta aberta no grafico (`Metrica.chave`), ou nenhuma. */
  metricaAberta: string | null;
  /** Periodo escolhido no grafico. So em memoria: recarregar volta a 24 h. */
  periodoGrafico: PeriodoGrafico;
  uploads: Record<string, UploadOut[]>;
  /**
   * Fotos sob demanda (contrato §7, `kind: "snapshot"`), separadas do acervo
   * horario. Duas listas e nao uma com filtro porque as duas telas tem ritmos
   * diferentes: o scrubber do timelapse percorre centenas de frames, a aba "Ao
   * vivo" so quer o mais recente.
   */
  snapshots: Record<string, UploadOut[]>;
  /**
   * Por aparelho, o ULID do comando `snapshot` que ainda espera resposta — e
   * `null` quando nao ha captura em curso.
   *
   * Guarda o ULID e nao um booleano porque e por ele que a foto certa e
   * reconhecida: o nome do objeto que volta e esse mesmo ULID (§7), entao duas
   * abas pedindo ao mesmo tempo nao confundem uma foto com a outra.
   */
  capturando: Record<string, string | null>;
  /** Janela pedida no ultimo GET de uploads. So em memoria: recarregar volta a 24 h. */
  periodoTimelapse: PeriodoTimelapse;
  comandos: Record<string, CommandOut[]>;
  /**
   * Por aparelho, as releases que ELE pode instalar (`GET .../firmware`).
   *
   * So em memoria, e de proposito: ao contrario dos uploads e dos comandos,
   * esta lista nao e historico do aparelho — e o catalogo do produto num
   * instante. Guardada em disco, uma aba aberta offline ofereceria "atualizar
   * para 1.3.0" para uma release que pode ter sido retirada desde entao, e o
   * botao levaria o usuario a um grant recusado.
   */
  releases: Record<string, ReleaseOut[]>;
  /**
   * Por aparelho, a versao de um `ota` ja publicado que o aparelho ainda nao
   * confirmou ter comecado — e `null` quando nao ha nenhum.
   *
   * Mesma forma de `capturando`, e pela mesma razao: entre o `201` do comando
   * e o primeiro sinal do aparelho existe uma janela em que a tela nao tem o
   * que mostrar. A diferenca e quem fecha a janela — aqui e o proprio
   * aparelho, quando o bloco `ota` do estado retido (§5) sai de `idle`.
   */
  atualizando: Record<string, string | null>;
  /** Aparelhos que mandaram lote de store-and-forward ha pouco (contrato §4). */
  backfill: Record<string, number>;
  /**
   * O feed de alertas do TENANT, mais novo primeiro — e nao um mapa por
   * aparelho.
   *
   * Uma lista so porque e assim que a tela le: "o que aconteceu na minha
   * estufa", com o nome do aparelho dentro de cada linha. Um mapa por aparelho
   * obrigaria a tela a costurar N listas por data toda vez que desenhasse, e o
   * badge — que fala do tenant inteiro — teria que somar todas elas.
   */
  alertas: AlertaOut[];
  /**
   * Quantos ainda nao foram lidos, segundo o SERVIDOR.
   *
   * Nao e `alertas.filter(read_at === null).length`: a lista guarda so os
   * ultimos `MAX_ALERTAS`, e o contador conta tudo o que existe. Deriva-lo da
   * lista faria o badge encolher sozinho conforme alertas antigos saissem da
   * cauda.
   */
  naoLidos: number;
  /**
   * Preferencia por tipo, como o servidor a conhece. `null` = ainda nao lida.
   *
   * So em memoria: e configuracao do servidor, nao historico. Guardada em
   * disco, uma aba offline mostraria um interruptor desligado que na verdade
   * outra pessoa ligou.
   */
  prefsNotificacao: PrefsNotificacao | null;
  /**
   * O que este NAVEGADOR consegue e quer fazer com notificacao.
   *
   * `suportado: false` cobre os dois casos que a tela precisa distinguir de
   * "desligado": navegador sem `Notification`/`PushManager`, e — o caso que
   * mais aparece — iOS com a PWA aberta na aba em vez de instalada na tela de
   * inicio. Ver `push.ts`.
   */
  push: { suportado: boolean; ligado: boolean; ocupado: boolean };
}

/**
 * Teto da serie do grafico. 700 baldes de 2 linhas cada, com folga — a maior
 * janela fixa (30 d) pede `bucket` na casa da hora e cabe bem dentro disto.
 * Nunca vai para o disco.
 */
const MAX_HISTORICO = 3000;

const estado: Estado = {
  sessao: null,
  conexao: "inicial",
  fonte: "cache",
  atualizadoEm: null,
  latenciaMs: null,
  aviso: null,
  aparelhos: [],
  selecionado: null,
  telemetria: {},
  serie: null,
  metricaAberta: null,
  periodoGrafico: { modo: "relativo", horas: 24 },
  uploads: {},
  snapshots: {},
  capturando: {},
  periodoTimelapse: { modo: "relativo", dias: 1 },
  comandos: {},
  releases: {},
  atualizando: {},
  backfill: {},
  alertas: [],
  naoLidos: 0,
  prefsNotificacao: null,
  push: { suportado: false, ligado: false, ocupado: false },
};

export const obter = (): Readonly<Estado> => estado;

export function aparelhoSelecionado(): AparelhoNoSnapshot | null {
  if (estado.selecionado === null) return null;
  return estado.aparelhos.find((a) => a.device.id === estado.selecionado) ?? null;
}

// --- assinatura --------------------------------------------------------

type Ouvinte = () => void;
const ouvintes = new Set<Ouvinte>();
let agendado = false;

export function assinar(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/**
 * Notifica uma vez por microtask.
 *
 * Um snapshot com dez aparelhos dispara dez mutacoes seguidas; sem isto, dez
 * renderizacoes completas. O `queueMicrotask` junta tudo o que acontecer no
 * mesmo processamento de quadro numa unica passada.
 */
function mudou(): void {
  if (agendado) return;
  agendado = true;
  queueMicrotask(() => {
    agendado = false;
    for (const ouvinte of ouvintes) ouvinte();
    agendarPersistencia();
  });
}

// --- persistencia ------------------------------------------------------

let timerPersistencia: number | null = null;

function agendarPersistencia(): void {
  if (estado.sessao === null || timerPersistencia !== null) return;
  timerPersistencia = window.setTimeout(() => {
    timerPersistencia = null;
    persistir();
  }, 1000);
}

/** Grava agora. Chamado tambem no `pagehide`, quando nao ha proximo timer. */
export function persistir(): void {
  if (estado.sessao === null) return;
  cache.gravarInstantaneo(estado.sessao.tenant_id, {
    aparelhos: estado.aparelhos,
    selecionado: estado.selecionado,
    telemetria: estado.telemetria,
    uploads: estado.uploads,
    snapshots: estado.snapshots,
    comandos: estado.comandos,
    alertas: estado.alertas,
    naoLidos: estado.naoLidos,
  });
}

// --- sessao ------------------------------------------------------------

/** Sessao confirmada pelo servidor (`GET /v1/auth/me`). */
export function definirSessao(usuario: UserOut): void {
  const lembrada = cache.lerSessao();
  if (lembrada !== null && lembrada.tenant_id !== usuario.tenant_id) {
    // Trocou de conta: o cache da anterior nao pode aparecer nem por um quadro.
    cache.limpar();
    zerarDados();
  }
  estado.sessao = {
    tenant_id: usuario.tenant_id,
    tenant_name: usuario.tenant_name,
    email: usuario.email,
    tier: usuario.tier,
  };
  cache.gravarSessao(estado.sessao);
  mudou();
}

/**
 * Abre com o que ficou em disco. Devolve `false` quando nao ha nada guardado.
 *
 * Chamado ANTES de qualquer rede: e o que faz a primeira pintura acontecer
 * offline. Tudo que entra por aqui fica com `fonte: "cache"` ate o primeiro
 * snapshot do relay chegar.
 */
export function restaurarDoCache(): boolean {
  const sessao = cache.lerSessao();
  if (sessao === null) return false;
  estado.sessao = sessao;

  const guardado = cache.lerInstantaneo(sessao.tenant_id);
  if (guardado === null) {
    mudou();
    return false;
  }
  estado.aparelhos = guardado.aparelhos;
  estado.selecionado = guardado.selecionado;
  estado.telemetria = guardado.telemetria;
  estado.uploads = guardado.uploads;
  // `?? {}` e nao `guardado.snapshots` seco: um instantaneo gravado por uma
  // versao anterior da PWA nao tem o campo, e a aba "Ao vivo" nasceria com
  // `undefined` no lugar de uma lista vazia.
  estado.snapshots = guardado.snapshots ?? {};
  estado.comandos = guardado.comandos;
  // `?? []` pelo mesmo motivo dos snapshots acima: um instantaneo gravado por
  // uma versao anterior da PWA nao tem os campos da C12.
  estado.alertas = guardado.alertas ?? [];
  estado.naoLidos = guardado.naoLidos ?? 0;
  estado.fonte = "cache";
  estado.atualizadoEm = guardado.gravadoEm;
  mudou();
  return true;
}

export function encerrarSessao(): void {
  cache.limpar();
  estado.sessao = null;
  zerarDados();
  estado.conexao = "inicial";
  mudou();
}

function zerarDados(): void {
  estado.aparelhos = [];
  estado.selecionado = null;
  estado.telemetria = {};
  estado.serie = null;
  estado.metricaAberta = null;
  estado.periodoGrafico = { modo: "relativo", horas: 24 };
  estado.uploads = {};
  estado.snapshots = {};
  estado.capturando = {};
  estado.periodoTimelapse = { modo: "relativo", dias: 1 };
  estado.comandos = {};
  estado.releases = {};
  estado.atualizando = {};
  estado.backfill = {};
  estado.alertas = [];
  estado.naoLidos = 0;
  estado.prefsNotificacao = null;
  // `push.suportado` NAO e zerado: e capacidade do navegador, nao dado da
  // conta. Trocar de usuario nao faz o aparelho deixar de ter bandeja de
  // notificacao.
  estado.push = { ...estado.push, ligado: false, ocupado: false };
  estado.fonte = "cache";
  estado.atualizadoEm = null;
  estado.aviso = null;
}

// --- conexao -----------------------------------------------------------

export function definirConexao(novo: EstadoConexao): void {
  if (estado.conexao === novo) return;
  estado.conexao = novo;
  mudou();
}

export function definirLatencia(ms: number): void {
  estado.latenciaMs = ms;
  mudou();
}

export function avisar(mensagem: string | null): void {
  estado.aviso = mensagem;
  mudou();
}

/** Todo quadro do relay passa por aqui: e o que da idade ao que esta na tela. */
function tocouAgora(): void {
  estado.fonte = "ao-vivo";
  estado.atualizadoEm = Date.now();
}

// --- dados -------------------------------------------------------------

/**
 * Passa para a linha do aparelho o que o digital twin sabe melhor.
 *
 * `devices.dev_name` nasce `'grow'` no enrollment (app/routers/enroll.py). Desde
 * a C10 o ingest grava ali o nome que vem no `state` retido (§5), mas isto
 * continua: um aparelho que enrolou e ainda nao publicou `state` so tem "grow"
 * no banco, e o twin e sempre a fonte mais fresca das duas. `fw` idem — o
 * `state` traz a versao junto com o resto.
 */
function absorverTwin(alvo: AparelhoNoSnapshot): void {
  const twin = alvo.state;
  if (twin === null) return;
  if (typeof twin.dev_name === "string" && twin.dev_name !== "") {
    alvo.device.dev_name = twin.dev_name;
  }
  if (typeof twin.fw === "string") alvo.device.fw_version = twin.fw;
}

export function aplicarSnapshot(aparelhos: AparelhoNoSnapshot[]): void {
  for (const alvo of aparelhos) absorverTwin(alvo);
  estado.aparelhos = aparelhos;
  if (
    estado.selecionado === null ||
    !aparelhos.some((a) => a.device.id === estado.selecionado)
  ) {
    estado.selecionado = aparelhos[0]?.device.id ?? null;
  }
  tocouAgora();
  mudou();
}

export function selecionar(deviceId: string): void {
  if (estado.selecionado === deviceId) return;
  estado.selecionado = deviceId;
  mudou();
}

function comAparelho(deviceId: string, fn: (a: AparelhoNoSnapshot) => void): void {
  const alvo = estado.aparelhos.find((a) => a.device.id === deviceId);
  if (alvo === undefined) return;
  fn(alvo);
}

export function aplicarEstado(
  deviceId: string,
  novo: EstadoAparelho,
  atualizadoEm: string,
): void {
  comAparelho(deviceId, (a) => {
    a.state = novo;
    a.state_updated_at = atualizadoEm;
    absorverTwin(a);
    conferirAtualizacao(a);
  });
  tocouAgora();
  mudou();
}

export function aplicarPresenca(
  deviceId: string,
  online: boolean,
  fw: string | null,
): void {
  comAparelho(deviceId, (a) => {
    a.device.online = online;
    if (online) a.device.last_seen = new Date().toISOString();
    if (fw !== null) a.device.fw_version = fw;
    conferirAtualizacao(a);
  });
  tocouAgora();
  mudou();
}

/**
 * Solta o "pedimos, ainda nao sabemos nada" quando o aparelho passa a falar.
 *
 * Duas saidas, e as duas sao o APARELHO assumindo a narrativa:
 *
 * 1. O bloco `ota` do estado retido saiu de `idle` — dai em diante quem diz o
 *    que esta acontecendo (`baixando 42%`, `gravando`) e ele, com informacao
 *    que a nuvem nao tem.
 * 2. A versao em execucao virou a que foi pedida — convergiu, e nao ha mais
 *    nada a esperar. Este e o caminho de quem estava com a aba fechada durante
 *    o download inteiro.
 *
 * Fica aqui e nao na view pela regra da mutacao unica: `atualizando` tem um
 * dono, e e este arquivo. Mesma forma do `capturando` fechado por
 * `aplicarUpload()`.
 */
function conferirAtualizacao(alvo: AparelhoNoSnapshot): void {
  const alvoDaVez = estado.atualizando[alvo.device.id];
  if (alvoDaVez == null) return;
  const estadoOta = alvo.state?.ota?.state;
  if (estadoOta !== undefined && estadoOta !== "idle") {
    estado.atualizando[alvo.device.id] = null;
    return;
  }
  if (alvo.device.fw_version !== null && mesmaVersao(alvo.device.fw_version, alvoDaVez)) {
    estado.atualizando[alvo.device.id] = null;
  }
}

/**
 * `1.2.0` e `v1.2.0` sao a mesma versao.
 *
 * O `fw` que o aparelho reporta e o `esp_app_desc.version` CRU (`git describe`),
 * enquanto a release e semver canonico — a mesma tolerancia que
 * `api/app/services/firmware.py::ordem()` aplica do outro lado. Comparar as
 * duas strings cruas deixaria a tela dizendo "atualizando…" para sempre num
 * aparelho que ja atualizou.
 */
const mesmaVersao = (a: string, b: string): boolean =>
  a.replace(/^v/, "") === b.replace(/^v/, "");

export function definirReleases(deviceId: string, lista: ReleaseOut[]): void {
  estado.releases[deviceId] = lista;
  mudou();
}

/** Marca (ou desmarca, com `null`) a atualizacao pedida e ainda sem sinal. */
export function definirAtualizacao(deviceId: string, versao: string | null): void {
  estado.atualizando[deviceId] = versao;
  mudou();
}

export function aplicarTelemetria(
  deviceId: string,
  amostras: TelemetrySample[],
  backfill: boolean,
): void {
  if (backfill) {
    // Lote de store-and-forward: os `ts` sao de dias atras (contrato §4) e
    // empurra-los para o buffer faria a sparkline saltar para o passado e o
    // "ultimo valor" da tela virar historia. Registra que chegou, so.
    estado.backfill[deviceId] = Date.now();
    tocouAgora();
    mudou();
    return;
  }

  emendar(deviceId, amostras);
  tocouAgora();
  mudou();
}

/**
 * Historico vindo do `GET .../telemetry`, emendado na serie que ja existe.
 *
 * Nao substitui: o socket pode ter entregue amostras enquanto este GET estava
 * no ar, e trocar a lista inteira as perderia. Nao chama `tocouAgora()` pelo
 * mesmo motivo que `definirUploads`/`definirComandos` nao chamam — quem da
 * idade ao que esta na tela e o relay. Uma resposta REST que chega com o socket
 * caido nao torna a tela "ao vivo", e a barra de estado continuaria mentindo se
 * tornasse.
 */
export function definirTelemetria(deviceId: string, amostras: TelemetrySample[]): void {
  emendar(deviceId, amostras);
  mudou();
}

/**
 * Chave que identifica uma serie do grafico: aparelho + periodo.
 *
 * Trocar de aparelho OU de periodo e trocar de chave — e e a chave, nao o
 * `deviceId` sozinho, que `acoes.acompanharGrafico()` observa para saber se
 * precisa de um GET novo. Exportada porque `acoes.ts` monta a mesma chave dos
 * dois lados: antes de pedir (para saber se ja tem) e depois de receber (para
 * descartar uma resposta que chegou atrasada, depois que o usuario ja trocou
 * de novo).
 */
export const chaveSerie = (deviceId: string, periodo: PeriodoGrafico): string =>
  `${deviceId}|${JSON.stringify(periodo)}`;

/**
 * Serie do grafico vinda do `GET .../telemetry`, para a `chave` pedida.
 *
 * SUBSTITUI, e nao emenda: ao contrario de `definirTelemetria`, aqui uma
 * chave nova (outro aparelho, ou outro periodo — inclusive outro `bucket`) e
 * uma serie DIFERENTE, e casar `ts` por coincidencia entre uma janela crua e
 * uma agregada daria uma linha sem sentido. `acoes.ts` e quem garante que a
 * chave ainda e a atual antes de chamar isto — uma resposta de uma janela ja
 * trocada e descartada la, nao aqui.
 */
export function definirSerie(chave: string, amostras: TelemetrySample[]): void {
  estado.serie = { chave, amostras: amostras.slice(-MAX_HISTORICO) };
  mudou();
}

/** Abre a metrica no grafico; a mesma de novo fecha. */
export function abrirMetrica(chave: string | null): void {
  estado.metricaAberta = estado.metricaAberta === chave ? null : chave;
  mudou();
}

export function definirPeriodoGrafico(periodo: PeriodoGrafico): void {
  estado.periodoGrafico = periodo;
  mudou();
}

/** Casa por `ts`, ordena crescente e guarda so a cauda. UM lugar so. */
function juntar(
  anterior: TelemetrySample[],
  novas: TelemetrySample[],
  teto: number,
): TelemetrySample[] {
  const porTs = new Map(anterior.map((a) => [a.ts, a]));
  for (const amostra of novas) porTs.set(amostra.ts, amostra);
  return [...porTs.values()].sort((a, b) => a.ts - b.ts).slice(-teto);
}

/**
 * Toda amostra ao vivo entra na cauda curta, e na serie do grafico quando ela
 * e a serie aberta.
 *
 * So entra na serie quando o periodo e RELATIVO: uma janela `datas` tem ponta
 * final fixa no passado, e uma amostra de agora nao pertence a ela so porque
 * o aparelho e o mesmo. Uma janela relativa tambem so recebe se a chave bate
 * — outro aparelho, ou outro `bucket` por outro periodo, e o `ts` que chega
 * nao teria com o que casar.
 */
function emendar(deviceId: string, amostras: TelemetrySample[]): void {
  estado.telemetria[deviceId] = juntar(
    estado.telemetria[deviceId] ?? [],
    amostras,
    MAX_AMOSTRAS,
  );
  const serieAtual = estado.serie;
  if (
    serieAtual === null ||
    estado.periodoGrafico.modo !== "relativo" ||
    serieAtual.chave !== chaveSerie(deviceId, estado.periodoGrafico)
  ) {
    return;
  }
  estado.serie = {
    chave: serieAtual.chave,
    amostras: juntar(serieAtual.amostras, amostras, MAX_HISTORICO),
  };
}

export function aplicarComando(deviceId: string, comando: CommandOut): void {
  const anterior = estado.comandos[deviceId] ?? [];
  // Chega duas vezes por comando — no publish e no ack (`app/schemas/ws.py`).
  // Casa pelo ULID, que e o mesmo nas duas.
  const semEle = anterior.filter((c) => c.id !== comando.id);
  estado.comandos[deviceId] = [comando, ...semEle].slice(0, 20);
  if (comando.ok === false && comando.err !== null) {
    estado.aviso = `${comando.cmd} ${comando.key}: ${comando.err}`;
  }
  tocouAgora();
  mudou();
}

export function definirComandos(deviceId: string, lista: CommandOut[]): void {
  estado.comandos[deviceId] = lista;
  mudou();
}

/**
 * Setter unico de imagem, para os dois `kind`.
 *
 * O `kind` escolhe a lista, e nao o chamador: o quadro do socket e a resposta
 * do `GET` chegam por caminhos diferentes e nenhum dos dois precisa saber onde
 * a imagem mora. Uma foto que chega fecha a captura pendente daquele aparelho
 * — mas so se for a foto DAQUELA captura, comparada pelo ULID que o nome do
 * objeto carrega (§7).
 */
export function aplicarUpload(deviceId: string, upload: UploadOut): void {
  const snapshot = upload.kind === "snapshot";
  const mapa = snapshot ? estado.snapshots : estado.uploads;
  const anterior = mapa[deviceId] ?? [];
  const semEle = anterior.filter((u) => u.object_key !== upload.object_key);
  mapa[deviceId] = ordenar([...semEle, upload]);

  if (snapshot && estado.capturando[deviceId] === ulidDaChave(upload.object_key)) {
    estado.capturando[deviceId] = null;
  }
  tocouAgora();
  mudou();
}

export function definirUploads(deviceId: string, lista: UploadOut[]): void {
  estado.uploads[deviceId] = ordenar(lista);
  mudou();
}

export function definirSnapshots(deviceId: string, lista: UploadOut[]): void {
  estado.snapshots[deviceId] = ordenar(lista);
  mudou();
}

/**
 * Marca (ou desmarca, com `null`) a captura em curso de um aparelho.
 *
 * `null` chega de tres lugares: a foto correspondente chegou, o comando falhou,
 * ou o tempo acabou. Todos passam por aqui.
 */
export function definirCaptura(deviceId: string, cmdId: string | null): void {
  estado.capturando[deviceId] = cmdId;
  mudou();
}

/** `snapshot/{tenant}/{device}/{ULID}.jpg` -> `{ULID}` (§7). */
const ulidDaChave = (chave: string): string =>
  chave.slice(chave.lastIndexOf("/") + 1).replace(/\.jpg$/, "");

export function definirPeriodoTimelapse(periodo: PeriodoTimelapse): void {
  estado.periodoTimelapse = periodo;
  mudou();
}

/**
 * Cronologico crescente.
 *
 * O `GET .../uploads` devolve mais recente primeiro (bom para uma lista); o
 * scrubber de timelapse quer o contrario, e a ordem lexicografica da chave ja
 * e a ordem cronologica por construcao (`gfc/core/timelapse_index.h`).
 */
const ordenar = (lista: UploadOut[]): UploadOut[] =>
  [...lista].sort((a, b) => a.captured_at.localeCompare(b.captured_at));

// --- alertas (C12) -----------------------------------------------------

/**
 * Um alerta que acabou de nascer, vindo do relay.
 *
 * Encaixa no topo e conta mais um nao-lido. Idempotente pelo `id`: o socket
 * pode reentregar (a mesma disciplina do `aplicarUpload`), e um alerta contado
 * duas vezes deixaria o badge mentindo para sempre — ele so zera quando o
 * usuario abre a lista, entao um erro aqui nao se corrige sozinho.
 */
export function aplicarAlerta(alerta: AlertaOut): void {
  if (estado.alertas.some((a) => a.id === alerta.id)) return;
  estado.alertas = [alerta, ...estado.alertas].slice(0, MAX_ALERTAS_MEMORIA);
  if (alerta.read_at === null) estado.naoLidos += 1;
  tocouAgora();
  mudou();
}

/** O feed inteiro, do `GET /v1/alerts`. O contador vem do servidor junto. */
export function definirAlertas(alertas: AlertaOut[], naoLidos: number): void {
  estado.alertas = alertas.slice(0, MAX_ALERTAS_MEMORIA);
  estado.naoLidos = naoLidos;
  mudou();
}

/**
 * Marca localmente o que o servidor acabou de marcar, e adota o contador dele.
 *
 * O contador vem do SERVIDOR e nao e recalculado aqui: quem sabe quantos
 * sobraram e quem executou o UPDATE — inclusive de alertas que nunca entraram
 * nesta lista.
 */
export function marcarAlertasLidos(naoLidos: number, ate: string): void {
  estado.alertas = estado.alertas.map((a) =>
    a.read_at === null && a.created_at <= ate ? { ...a, read_at: ate } : a,
  );
  estado.naoLidos = naoLidos;
  mudou();
}

/** Teto do feed em memoria. Espelha o `MAX_ALERTAS` do `cache.ts`. */
const MAX_ALERTAS_MEMORIA = 50;

export function definirPrefsNotificacao(prefs: PrefsNotificacao): void {
  estado.prefsNotificacao = prefs;
  mudou();
}

/** Capacidade e estado da inscricao deste navegador. Dono: `push.ts`. */
export function definirPush(parcial: Partial<Estado["push"]>): void {
  estado.push = { ...estado.push, ...parcial };
  mudou();
}
